<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { showFailToast, showSuccessToast } from "vant";

import { api, apiMessage } from "../api";
import QuestionCard from "../components/QuestionCard.vue";
import type { AbilityAnswer, Question, SessionInfo } from "../types";

const route = useRoute();
const router = useRouter();
const token = String(route.params.token);
const session = ref<SessionInfo | null>(null);
const moduleIndex = ref(0);
const questions = ref<Question[]>([]);
const answers = ref<Record<number, unknown>>({});
const page = ref(0);
const loading = ref(true);
const saving = ref(false);
const pageOpenedAt = ref(Date.now());
const moduleOpenedAt = ref(Date.now());
const dirtyQuestionIds = ref(new Set<number>());
const savedQuestionIds = ref(new Set<number>());
const answerCache = new Map<string, Record<number, unknown>>();
const dirtyCache = new Map<string, Set<number>>();
let autoAdvanceTimer: number | undefined;

const currentModule = computed(() => session.value?.modules[moduleIndex.value]);
const pageCount = computed(() => questions.value.length);
const currentQuestion = computed(() => questions.value[page.value]);
const isFirstQuestion = computed(() => moduleIndex.value === 0 && page.value === 0);
const isLastQuestion = computed(
  () =>
    moduleIndex.value === (session.value?.modules.length || 1) - 1 &&
    page.value === pageCount.value - 1,
);
const currentIsComplete = computed(() =>
  currentQuestion.value ? isAnswered(currentQuestion.value) : false,
);
const currentIsSaved = computed(() => {
  const question = currentQuestion.value;
  if (!question) return false;
  return savedQuestionIds.value.has(question.id) && !dirtyQuestionIds.value.has(question.id);
});
const displayProgress = computed(() => {
  return session.value?.progress || 0;
});

function isAnswered(question: Question): boolean {
  const value = answers.value[question.id];
  if (question.kind === "value_group") return Array.isArray(value) && value.length === 5;
  if (question.kind === "ability_profile") {
    const profile = value as AbilityAnswer | undefined;
    return profile?.strong?.length === 5 && profile?.weak?.length === 5;
  }
  if (question.kind === "reflection_choice") return typeof value === "string" && value.length > 0;
  return typeof value === "number";
}

function cancelAutoAdvance() {
  if (autoAdvanceTimer !== undefined) {
    window.clearTimeout(autoAdvanceTimer);
    autoAdvanceTimer = undefined;
  }
}

function cacheCurrentModule() {
  if (!currentModule.value || !questions.value.length) return;
  answerCache.set(currentModule.value.code, { ...answers.value });
  dirtyCache.set(currentModule.value.code, new Set(dirtyQuestionIds.value));
}

async function loadModule(index: number, restorePage = false) {
  if (!session.value) return;
  cancelAutoAdvance();
  cacheCurrentModule();
  moduleIndex.value = index;
  loading.value = true;
  try {
    const module = session.value.modules[index];
    if (!module) {
      showFailToast("测评模块不存在");
      return;
    }
    const { data } = await api.get(`/public/sessions/${token}/modules/${module.code}/questions`);
    questions.value = data.questions;
    const persistedAnswers = Object.fromEntries(
      questions.value.filter((item) => item.answer != null).map((item) => [item.id, item.answer]),
    );
    answers.value = { ...persistedAnswers, ...(answerCache.get(module.code) || {}) };
    savedQuestionIds.value = new Set(
      questions.value.filter((item) => item.answer != null).map((item) => item.id),
    );
    dirtyQuestionIds.value = new Set(dirtyCache.get(module.code) || []);
    if (restorePage) {
      const firstMissing = questions.value.findIndex((item) => !isAnswered(item));
      page.value = firstMissing < 0 ? Math.max(0, pageCount.value - 1) : firstMissing;
    } else {
      page.value = 0;
    }
    pageOpenedAt.value = Date.now();
    moduleOpenedAt.value = Date.now();
  } catch (error) {
    showFailToast(apiMessage(error));
  } finally {
    loading.value = false;
  }
}

function updateAnswer(question: Question, value: unknown) {
  answers.value[question.id] = value;
  dirtyQuestionIds.value = new Set(dirtyQuestionIds.value).add(question.id);
  cancelAutoAdvance();
  if (!isAnswered(question)) return;
  autoAdvanceTimer = window.setTimeout(() => {
    autoAdvanceTimer = undefined;
    void saveAndAdvance(question.id);
  }, 420);
}

async function persistQuestion(question: Question): Promise<boolean> {
  if (!dirtyQuestionIds.value.has(question.id)) return true;
  if (!isAnswered(question)) {
    showFailToast("请先完成当前题目");
    return false;
  }
  saving.value = true;
  const elapsed = Math.max(200, Date.now() - pageOpenedAt.value);
  try {
    const { data } = await api.put(`/public/sessions/${token}/answers`, {
      answers: [
        {
          question_id: question.id,
          value: answers.value[question.id],
          duration_ms: elapsed,
        },
      ],
      current_module: currentModule.value?.code,
      module_duration_ms: Date.now() - moduleOpenedAt.value,
    });
    const nextDirty = new Set(dirtyQuestionIds.value);
    nextDirty.delete(question.id);
    dirtyQuestionIds.value = nextDirty;
    savedQuestionIds.value = new Set(savedQuestionIds.value).add(question.id);
    if (session.value) {
      session.value.progress = data.progress;
      const module = session.value.modules[moduleIndex.value];
      if (module) {
        module.answered_count = questions.value.filter((item) =>
          savedQuestionIds.value.has(item.id),
        ).length;
        module.completed = module.answered_count === module.question_count;
      }
    }
    return true;
  } catch (error) {
    showFailToast(apiMessage(error));
    return false;
  } finally {
    saving.value = false;
  }
}

function resetQuestionViewport() {
  pageOpenedAt.value = Date.now();
  window.requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "auto" }));
}

async function advance() {
  if (page.value < pageCount.value - 1) {
    page.value += 1;
    resetQuestionViewport();
    return;
  }
  if (session.value && moduleIndex.value < session.value.modules.length - 1) {
    await loadModule(moduleIndex.value + 1);
    resetQuestionViewport();
    showSuccessToast("已进入下一模块");
    return;
  }
  const { data } = await api.post(`/public/sessions/${token}/complete`);
  if (data.status === "verification_required") {
    const sessionResponse = await api.get<SessionInfo>(`/public/sessions/${token}`);
    session.value = sessionResponse.data;
    const verificationIndex = session.value.modules.findIndex(
      (item) => item.code === data.module,
    );
    if (verificationIndex >= 0) {
      await loadModule(verificationIndex);
      resetQuestionViewport();
      showSuccessToast(data.message || "已进入补充校准");
      return;
    }
  }
  await router.replace(`/summary/${token}`);
}

async function saveAndAdvance(expectedQuestionId?: number) {
  if (saving.value) return;
  cancelAutoAdvance();
  const question = currentQuestion.value;
  if (!question || (expectedQuestionId && question.id !== expectedQuestionId)) return;
  if (!isAnswered(question)) {
    showFailToast("请先完成当前题目");
    return;
  }
  if (!(await persistQuestion(question))) return;
  try {
    await advance();
  } catch (error) {
    showFailToast(apiMessage(error));
  }
}

async function previous() {
  if (saving.value) return;
  cancelAutoAdvance();
  const question = currentQuestion.value;
  if (question && dirtyQuestionIds.value.has(question.id) && isAnswered(question)) {
    if (!(await persistQuestion(question))) return;
  }
  if (page.value > 0) {
    page.value -= 1;
    resetQuestionViewport();
    return;
  }
  if (moduleIndex.value > 0) {
    await loadModule(moduleIndex.value - 1);
    page.value = Math.max(0, pageCount.value - 1);
    resetQuestionViewport();
  }
}

onMounted(async () => {
  try {
    const { data } = await api.get<SessionInfo>(`/public/sessions/${token}`);
    session.value = data;
    if (data.status === "completed") {
      await router.replace(`/summary/${token}`);
      return;
    }
    const current = Math.max(
      0,
      data.modules.findIndex((item) => item.code === data.current_module),
    );
    await loadModule(current, true);
  } catch (error) {
    loading.value = false;
    showFailToast(apiMessage(error));
  }
});

onBeforeUnmount(cancelAutoAdvance);
</script>

<template>
  <main class="assessment-page">
    <header class="assessment-header">
      <div>
        <span class="section-kicker">模块 {{ moduleIndex + 1 }} / {{ session?.modules.length || 7 }}</span>
        <h1>{{ currentModule?.name || "加载中" }}</h1>
      </div>
      <strong>{{ displayProgress.toFixed(1) }}%</strong>
    </header>
    <van-progress :percentage="displayProgress" :show-pivot="false" color="#27715e" />

    <section v-if="currentModule" class="module-intro">
      <p>{{ currentModule.description }}</p>
      <span>本题 {{ page + 1 }} / {{ pageCount }}</span>
    </section>

    <van-skeleton v-if="loading" title :row="8" />
    <template v-else>
      <Transition name="question-shift" mode="out-in">
        <QuestionCard
          v-if="currentQuestion"
          :key="currentQuestion.id"
          :model-value="answers[currentQuestion.id]"
          :question="currentQuestion"
          @update:model-value="updateAnswer(currentQuestion, $event)"
        />
      </Transition>
      <div class="save-status" :class="{ saved: currentIsSaved }" aria-live="polite">
        <span></span>
        {{
          saving
            ? "正在保存答案…"
            : currentIsSaved
              ? "答案已保存，可返回修改"
              : currentIsComplete
                ? "即将保存并进入下一题"
                : "选择后自动保存并进入下一题"
        }}
      </div>
      <div class="assessment-actions">
        <van-button plain round :disabled="isFirstQuestion || saving" @click="previous">
          上一题
        </van-button>
        <van-button
          type="primary"
          round
          :disabled="!currentIsComplete"
          :loading="saving"
          @click="saveAndAdvance()"
        >
          {{ isLastQuestion ? "完成测评" : "下一题" }}
        </van-button>
      </div>
      <p class="autosave-note">每题保存成功后才会跳转；可随时返回修改，并使用原专属链接断点续答。</p>
    </template>
  </main>
</template>
